import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Select, TextField, Typography, Grid, IconButton, ToggleButton, ToggleButtonGroup, InputAdornment
} from '@mui/material';
import { useFormik } from 'formik';
import { Save, Close, Info as InfoIcon } from '@mui/icons-material';
import { alpha, Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { DesktopDatePicker } from '@mui/lab';
import { theme } from '../../../../../styles';
import { ILicense } from '../../../../../models/smp';
import { SMPAPI } from '../../../../../fetch/smp';
import { useToastContext } from '../../../../../hooks';
import { ToastV3, CurrencyFormatter } from '../../../components';

interface AddEditLicenseModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: FormValues) => void;
  initialData?: ILicense;
  applicationID: number;
}

interface FormValues {
  licenseID?: any;
  applicationID: number;
  isContract: boolean;
  isSubscription: boolean;
  licenseName?: string;
  licenseTypeID?: number;
  licenseStatusID?: number;
  startDate?: Date | null;
  endDate?: Date | null;
  cancelByDate?: Date | null;
  renewByDate?: Date | null;
  autoRenew?: 'Y' | 'N';
  renewalTermID?: number;
  annualCost?: string;
  seatQuantity?: string;
  creditQuantity?: string;
  totalValue?: string;
  monthlyContractValue?: string;
  annualCostPerUnit?: string;
  paymentTermID?: number;
  paymentMethodID?: number;
  initialPaymentDate?: Date | null;
  approvalNotes?: string;
  otherNotes?: string;
}

const yesNoOptions = [
  { label: 'Yes', value: 'Y' },
  { label: 'No',  value: 'N' },
];

type DateFieldKey = 'startDate' | 'endDate' | 'cancelByDate' | 'renewByDate';
const dateLabels: Record<DateFieldKey, string> = {
  startDate: 'Start Date',
  endDate: 'End Date',
  cancelByDate: 'Cancel By Date',
  renewByDate: 'Renew By Date',
};

function mapInitialDataToFormik(data?: ILicense): Partial<FormValues> {
  if (!data) return {};
  const seatQtyNum = data.seatQuantity ? parseFloat(data.seatQuantity) : 0;
  const creditQtyNum = data.creditQuantity ? parseFloat(data.creditQuantity) : 0;
  return {
    isContract: !!data.isContract,
    isSubscription: !!data.isSubscription,
    licenseName: data.licenseName || '',
    licenseTypeID: data.licenseTypeID ?? 0,
    licenseStatusID: data.licenseStatusID ?? 0,
    startDate: data.startDate ? new Date(data.startDate) : null,
    endDate: data.endDate ? new Date(data.endDate) : null,
    cancelByDate: data.cancelByDate ? new Date(data.cancelByDate) : null,
    renewByDate: data.renewByDate ? new Date(data.renewByDate) : null,
    autoRenew: data.autoRenew === 'Y' ? 'Y' : 'N',
    renewalTermID: data.renewalTermID ?? 0,
    annualCost: String(data.annualCost ?? ''),
    seatQuantity: seatQtyNum > 0 ? String(seatQtyNum) : '',
    creditQuantity: creditQtyNum > 0 ? String(creditQtyNum) : '',
    totalValue: String(data.totalValue ?? ''),
    paymentTermID: data.paymentTermID ?? 0,
    paymentMethodID: data.paymentMethodID ?? 0,
    initialPaymentDate: data.initialPaymentDate ? new Date(data.initialPaymentDate) : null,
    approvalNotes: data.approvalNotes || '',
    otherNotes: data.otherNotes || '',
  };
}

export const AddEditLicenseModal: React.FC<AddEditLicenseModalProps> = ({
  open, onClose, onSave, initialData, applicationID,
}) => {
  const isNew = !initialData?.licenseID;
  const [mode, setMode] = useState<'view' | 'edit'>(isNew ? 'edit' : 'view');
  const classes = useStyles();
  const { showToast } = useToastContext();

  const [licenseTypes, setLicenseTypes] = useState<{ value: number; label: string }[]>([]);
  const [licenseStatuses, setLicenseStatuses] = useState<{ value: number; label: string }[]>([]);
  const [renewalTerms, setRenewalTerms] = useState<{ value: number; label: string }[]>([]);
  const [paymentTerms, setPaymentTerms] = useState<{ value: number; label: string }[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<{ value: number; label: string }[]>([]);
  const [showSaveReminder, setShowSaveReminder] = useState(false);

  const hasInitializedEditMode = useRef(false);
  const userHasInteracted = useRef(false);

  const initialValues: FormValues = useMemo(() => ({
    licenseID: initialData?.licenseID ?? '',
    applicationID,
    isContract: false,
    isSubscription: false,
    licenseName: '',
    licenseTypeID: undefined,
    licenseStatusID: undefined,
    startDate: null,
    endDate: null,
    cancelByDate: null,
    renewByDate: null,
    autoRenew: 'N',
    renewalTermID: undefined,
    annualCost: '',
    seatQuantity: '',
    creditQuantity: '',
    totalValue: '',
    paymentTermID: undefined,
    paymentMethodID: undefined,
    initialPaymentDate: null,
    approvalNotes: '',
    otherNotes: '',
    ...mapInitialDataToFormik(initialData),
  }), [initialData, applicationID]);

  const formik = useFormik<FormValues>({
    initialValues,
    enableReinitialize: true,

    validate: values => {
      const errors: Partial<Record<keyof FormValues, string>> = {};
  
      const { startDate, endDate, cancelByDate, renewByDate } = values;
  
      if (startDate && endDate && startDate > endDate) {
        errors.startDate = 'Start date must be on or before end date';
        errors.endDate = 'End date must be on or after start date';
      }
  
      if (cancelByDate && startDate && cancelByDate < startDate) {
        errors.cancelByDate = 'Cancel-by date cannot be before start date';
      }
      if (cancelByDate && endDate && cancelByDate > endDate) {
        errors.cancelByDate = 'Cancel-by date cannot be after end date';
      }
  
      if (renewByDate && startDate && renewByDate < startDate) {
        errors.renewByDate = 'Renew-by date cannot be before start date';
      }
  
  
      return errors;
    },


    onSubmit: rawValues => {
      if (!rawValues.isContract && !rawValues.isSubscription) {
        showToast('warning', 'Select either Contract or Subscription');
        return;
      }

      const sanitizeNumber = (val?: string): string | undefined => {
        const stripped = val?.toString().replace(/[^0-9.]/g, '');
        return stripped && !isNaN(Number(stripped)) ? stripped : undefined;
      };

      const cleaned: FormValues = {
        ...rawValues,
        annualCost: sanitizeNumber(rawValues.annualCost),
        totalValue: sanitizeNumber(rawValues.totalValue),
        monthlyContractValue: sanitizeNumber(rawValues.monthlyContractValue),
        annualCostPerUnit: sanitizeNumber(rawValues.annualCostPerUnit),
        seatQuantity: sanitizeNumber(rawValues.seatQuantity),
        creditQuantity: sanitizeNumber(rawValues.creditQuantity),
      };

      onSave({ ...cleaned, applicationID });
      setShowSaveReminder(false);
    },
  });

  const { values, setFieldValue, dirty } = formik;

  // Recalculate totalValue whenever cost or seats change

  useEffect(() => {
    const annualCost = parseFloat(values.annualCost || '0');
    const seatQty = parseFloat(values.seatQuantity || '0');
    const creditQty = parseFloat(values.creditQuantity || '0');
  
    const quantity = seatQty || creditQty;
    const totalValue = annualCost * quantity;
    const annualCostPerUnit = annualCost;
    const monthlyContractValue = totalValue / 12;
  
    setFieldValue('totalValue', totalValue.toFixed(2), false);
    setFieldValue('monthlyContractValue', monthlyContractValue.toFixed(2), false);
    setFieldValue('annualCostPerUnit', annualCostPerUnit.toFixed(2), false);
  }, [
    values.annualCost,
    values.seatQuantity,
    values.creditQuantity,
    setFieldValue,
  ]);

  // Reset mode on open/close
  useEffect(() => {
    if (open) {
      setMode(isNew ? 'edit' : 'view');
      hasInitializedEditMode.current = false;
      setShowSaveReminder(false);
    }
  }, [open, isNew]);

  // Fetch dropdowns
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [types, statuses, renewals, payments, methods] = await Promise.all([
          SMPAPI.fetchLicenseTypes(),
          SMPAPI.fetchLicenseStatuses(),
          SMPAPI.fetchRenewalTerms(),
          SMPAPI.fetchPaymentTerms(),
          SMPAPI.fetchPaymentMethods(),
        ]);
        setLicenseTypes(types.map(t => ({ value: Number(t.value), label: t.label })));
        setLicenseStatuses(statuses.map(s => ({ value: Number(s.value), label: s.label })));
        setRenewalTerms(renewals.map(r => ({ value: Number(r.value), label: r.label })));
        setPaymentTerms(payments.map(p => ({ value: Number(p.value), label: p.label })));
        setPaymentMethods(methods.map(m => ({ value: Number(m.value), label: m.label })));
      } catch (error) {
        //console.error('Failed to fetch dropdown options:', error);
        showToast('error', 'Failed to load options');
      }
    })();
  }, [open, showToast]);

  // Prompt to save on edits
  useEffect(() => {
    if (mode !== 'edit' || !open || isNew) return;
    if (!hasInitializedEditMode.current) { hasInitializedEditMode.current = true; return; }
    if (!userHasInteracted.current) return;
    setShowSaveReminder(dirty);
  }, [dirty, mode, open, isNew]);

  const denseLabelStyle = {
    fontWeight: 500,
    fontSize: '0.85rem',
    color: '#333',
    display: 'flex',
    alignItems: 'center',
    height: '100%',
  };

  // ffield disabling - only based on mode
  const isFieldDisabled = (mode: 'view' | 'edit') => {
    return mode === 'view';
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          {isNew ? 'Add License / Subscription'
            : mode === 'view' ? 'License Details' : 'Edit License'}
        </Typography>
        {!isNew && (
          <ToggleButtonGroup
            value={mode}
            exclusive
            size="small"
            onChange={(_, v) => v && setMode(v)}
          >
            <ToggleButton value="view">View</ToggleButton>
            <ToggleButton value="edit">Edit</ToggleButton>
          </ToggleButtonGroup>
        )}
        <IconButton onClick={onClose}><Close /></IconButton>
      </DialogTitle>
  
      <form onSubmit={formik.handleSubmit}>
        <DialogContent sx={{ px: 2.5, py: 1 }}>
          <Box bgcolor="#f5f5f5" borderRadius={2} px={2} py={2}>
            {/* BASIC DETAILS */}
            <Box mb={2}>
              <Typography className={classes.header} variant="body1" sx={{ px: 1, py: 0.5, fontWeight: 'bold', borderRadius: 1 }}>
                Basic Details
              </Typography>
            </Box>
            <Grid container spacing={0.5}>
              {/* Checkboxes */}
              <Grid item xs={1} />
              <Grid item xs={1}>
                <Checkbox
                  disabled={mode === 'view'}
                  checked={formik.values.isContract}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    formik.setFieldValue('isContract', checked);
                    if (checked) formik.setFieldValue('isSubscription', false);
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <Typography sx={denseLabelStyle}>Contract</Typography>
              </Grid>
              <Grid item xs={1}>
                <Checkbox
                  disabled={mode === 'view'}
                  checked={formik.values.isSubscription}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    formik.setFieldValue('isSubscription', checked);
                    if (checked) formik.setFieldValue('isContract', false);
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <Typography sx={denseLabelStyle}>Subscription</Typography>
              </Grid>
              <Grid item xs={1} />

              {/* License Name */}
              <Grid item xs={1} />
              <Grid item xs={5}>
                <Typography sx={denseLabelStyle}>Contract / Subscription Name</Typography>
              </Grid>
              <Grid item xs={5}>
                <TextField
                  fullWidth
                  disabled={mode === 'view'}
                  name="licenseName"
                  value={formik.values.licenseName}
                  onChange={(e) => {
                    userHasInteracted.current = true;
                    formik.handleChange(e);
                  }}

                />
              </Grid>
              <Grid item xs={1} />

  
              {/* License Type */}
              <Grid item xs={1} />
              <Grid item xs={5}>
                <Typography sx={denseLabelStyle}>License Type</Typography>
              </Grid>
              <Grid item xs={5}>
                <Select
                  fullWidth
                  size="small"
                  disabled={mode === 'view'}
                  name="licenseTypeID"
                  value={String(formik.values.licenseTypeID)}
                  onChange={(e) => formik.setFieldValue('licenseTypeID', Number(e.target.value))}
                >
                  {licenseTypes.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={1} />
  
              {/* License Status */}
              <Grid item xs={1} />
              <Grid item xs={5}>
                <Typography sx={denseLabelStyle}>License Status</Typography>
              </Grid>
              <Grid item xs={5}>
                <Select
                  fullWidth size="small"
                  disabled={mode === 'view'}
                  name="licenseStatusID"
                  value={formik.values.licenseStatusID}
                  onChange={(e) => {
                    userHasInteracted.current = true;
                    formik.handleChange(e);
                  }}

                >
                  {licenseStatuses.map(o => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={1} />
  
              {/* Date fields - enabled for both contract and subscription */}
              {(Object.keys(dateLabels) as DateFieldKey[]).map(key => (
                <React.Fragment key={key}>
                  <Grid item xs={1} />
                  <Grid item xs={5}>
                    <Typography sx={denseLabelStyle}>{dateLabels[key]}</Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <DesktopDatePicker
                      disabled={isFieldDisabled(mode)}
                      inputFormat="M/d/yyyy"
                      allowSameDateSelection
                      value={formik.values[key]}
                      onChange={val => formik.setFieldValue(key, val)}
                      maxDate={
                        ['startDate', 'cancelByDate'].includes(key) ? values.endDate : undefined
                      }
                      minDate={
                        key !== 'startDate' ? values.startDate : undefined
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          disabled={isFieldDisabled(mode)}
                          error={Boolean(formik.touched[key] && formik.errors[key])}
                          helperText={formik.touched[key] && formik.errors[key]}
                          onBlur={() => formik.setFieldTouched(key, true)}
                        />
                      )}
                    />

                  </Grid>
                  <Grid item xs={1} />
                </React.Fragment>
              ))}
  
              {/* Auto Renew */}
              <Grid item xs={1} />
              <Grid item xs={5}>
                <Typography sx={denseLabelStyle}>Auto Renew</Typography>
              </Grid>
              <Grid item xs={5}>
                <Select
                  fullWidth size="small"
                  disabled={mode === 'view'}
                  name="autoRenew"
                  value={formik.values.autoRenew}
                  onChange={(e) => {
                    userHasInteracted.current = true;
                    formik.handleChange(e);
                  }}

                >
                  {yesNoOptions.map(o => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={1} />
  
              {/* Renewal Term */}
              <Grid item xs={1} />
              <Grid item xs={5}>
                <Typography sx={denseLabelStyle}>Renewal Term</Typography>
              </Grid>
              <Grid item xs={5}>
                <Select
                  fullWidth size="small"
                  disabled={mode === 'view'}
                  name="renewalTermID"
                  value={formik.values.renewalTermID}
                  onChange={(e) => {
                    userHasInteracted.current = true;
                    formik.handleChange(e);
                  }}

                >
                  {renewalTerms.map(o => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={1} />
            </Grid>
  
            {/* COST DETAILS */}
            <Box mt={3} mb={1.5}>
              <Typography className={classes.header} variant="body1" sx={{ px: 1, py: 0.5, fontWeight: 'bold', borderRadius: 1 }}>
                Cost Details
              </Typography>
            </Box>
            <Grid container spacing={0.5}>
              {/* Annual Cost, Seat Qty, Credit Qty */}
              {['annualCost', 'seatQuantity', 'creditQuantity'].map(field => (
                <React.Fragment key={field}>
                  {/* left gutter */}
                  <Grid item xs={1} />

                  {/* label */}
                  <Grid item xs={5}>
                    <Typography sx={denseLabelStyle}>
                      {field === 'annualCost'
                        ? 'Annual Cost Per User / Credit / Add-On'
                        : field === 'seatQuantity'
                          ? 'Seat Quantity'
                          : 'Credit Quantity'}
                    </Typography>
                  </Grid>

                  {/* the TextField itself */}
                  <Grid item xs={5}>
                    <TextField
                      fullWidth
                      disabled={mode === 'view'}
                      type="text"                             
                      name={field}
                      value={(formik.values as any)[field]}
                      onChange={e => {
                        const val = e.target.value;
                        if (/^\d*\.?\d{0,2}$/.test(val)) {
                          userHasInteracted.current = true;
                          formik.setFieldValue(field, val);
                          if (field === 'seatQuantity') formik.setFieldValue('creditQuantity', '');
                          else if (field === 'creditQuantity') formik.setFieldValue('seatQuantity', '');
                        }
                      }}
                      inputProps={{
                        inputMode: 'decimal',
                        pattern: '^\\d*(\\.\\d{0,2})?$',
                        min: 0,
                      }}
                    />
                  </Grid>

                  {/* right gutter */}
                  <Grid item xs={1} />

                  {field === 'annualCost' && (
                    <>
                      <Grid item xs={1} />
                      <Grid item xs={10}>
                        <Box
                          display="flex"
                          alignItems="center"
                          p={1}
                          bgcolor={alpha(theme.palette.info.main, 0.08)} 
                          borderRadius={1}
                        >
                          <InfoIcon fontSize="small" sx={{ mr: 1, color: '#1976d2' }} />
                          <Typography variant="caption" color="textSecondary">
                            Cost Details can only be determined by either Seat Quantity or Credit Quantity. Please only fill out one of these fields.
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={1} />
                    </>
                  )}
                </React.Fragment>
              ))}


  
              {/* Total Contract Value */}
              <Grid item xs={1} />
              <Grid item xs={5}>
                <Typography sx={denseLabelStyle}>Total Contract Value</Typography>
              </Grid>
              <Grid item xs={5}>
                <TextField
                  fullWidth
                  disabled
                  variant="outlined"
                  size="small"
                  // value empty; all formatting lives in the adornment
                  value=""
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <CurrencyFormatter value={formik.values.totalValue} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={1} />
            
            </Grid>
  
            {/** PAYMENT DETAILS **/}
            <Box mt={3} mb={1.5}>
              <Typography
                className={classes.header}
                variant="body1"
                sx={{ px: 1, py: 0.5, fontWeight: 'bold', borderRadius: 1 }}
              >
                Payment Details
              </Typography>
            </Box>

            <Grid container spacing={0.5}>
              {/* Payment Term */}
              <Grid item xs={1} />
              <Grid item xs={5}>
                <Typography sx={denseLabelStyle}>Payment Terms</Typography>
              </Grid>
              <Grid item xs={5}>
                <Select
                  fullWidth size="small"
                  disabled={mode === 'view'}
                  name="paymentTermID"
                  value={formik.values.paymentTermID}
                  onChange={(e) => {
                    userHasInteracted.current = true;
                    formik.handleChange(e);
                  }}

                >
                  {paymentTerms.map(o => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={1} />
  
              {/* Payment Method */}
              <Grid item xs={1} />
              <Grid item xs={5}>
                <Typography sx={denseLabelStyle}>Payment Method</Typography>
              </Grid>
              <Grid item xs={5}>
                <Select
                  fullWidth size="small"
                  disabled={mode === 'view'}
                  name="paymentMethodID"
                  value={formik.values.paymentMethodID}
                  onChange={(e) => {
                    userHasInteracted.current = true;
                    formik.handleChange(e);
                  }}

                >
                  {paymentMethods.map(o => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={1} />
  
              {/* Initial Payment Date */}
              <Grid item xs={1} />
              <Grid item xs={5}>
                <Typography sx={denseLabelStyle}>Initial Payment Date</Typography>
              </Grid>
              <Grid item xs={5}>
                <DesktopDatePicker
                  disabled={mode === 'view'}
                  inputFormat="M/d/yyyy"
                  value={formik.values.initialPaymentDate}
                  onChange={val => formik.setFieldValue('initialPaymentDate', val)}
                  renderInput={params => (
                    <TextField {...params} fullWidth disabled={mode === 'view'} />
                  )}
                />
              </Grid>
              <Grid item xs={1} />
            </Grid>
  
            {/* NOTES */}
            <Box mt={3} mb={1.5}>
              <Typography className={classes.header} variant="body1" sx={{ px: 1, py: 0.5, fontWeight: 'bold', borderRadius: 1 }}>
                Notes
              </Typography>
            </Box>
            {[
              { label: 'Approval Notes', name: 'approvalNotes' as keyof FormValues },
              { label: 'Other Notes', name: 'otherNotes' as keyof FormValues },
            ].map(({ label, name }) => (
              <Grid container spacing={1} sx={{ mb: 1 }} key={name}>
                <Grid item xs={1} />
                <Grid item xs={5}>
                  <Typography sx={denseLabelStyle}>{label}</Typography>
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    fullWidth
                    disabled={mode === 'view'}
                    multiline
                    minRows={4}
                    name={name}
                    value={formik.values[name]}
                    onChange={(e) => {
                      userHasInteracted.current = true;
                      formik.handleChange(e);
                    }}

                    size="small"
                  />
                </Grid>
                <Grid item xs={1} />
              </Grid>
            ))}
          </Box>
        </DialogContent>
  
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'flex-end' }}>
          {mode === 'edit' && (
            <>
              <Button variant="contained" onClick={onClose} startIcon={<Close />}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="secondary"
                type="submit"
                startIcon={<Save />}
              >
                Save
              </Button>
            </>
          )}
        </DialogActions>
      </form>
      {showSaveReminder && !isNew && (
        <Box sx={{ position: 'fixed', bottom: 0, left: 0, width: '100%' }}>
          <ToastV3
            type="warning"
            message="You have unsaved changes."
            confirmText="Save"
            onSave={formik.submitForm}
            onClose={() => setShowSaveReminder(false)}
            onCancel={() => { }}
            showReset={false}
          />
        </Box>
      )}


    </Dialog>
  );
};



export default AddEditLicenseModal;


const useStyles = makeStyles<Theme>(() => ({
  emphasis: {
    fontWeight: 600,
    color: theme.palette.secondary.main,
  },

  label: {
    backgroundColor: theme.palette.background.paper,
  },
  grid: {
    marginTop: theme.spacing(0.5),
    paddingTop: '0 !important',
  },
  readonly: {
    paddingTop: theme.spacing(0.5),
  },

  header: {
    backgroundColor: '#005570',
    color: '#eff4f6',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.25rem 1rem',
    fontWeight: 600,
  },
  noButton: {
    padding: '.704rem 1rem',
  },
  button: {
    border: '1px solid rgba(255, 255, 255, 0.5)',
    color: '#fff',
    '&:hover': {
      color: theme.frankie.searchBlue,
      borderColor: '#ffffff',
      backgroundColor: '#ffffff',
    },
  },
  resetButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
}));

